/* Лёгкие графики на HTML/CSS (перенос charts.js из макета): столбцы с накоплением, воронка, тепловая карта.
   У каждой отметки подсказка (tipProps), подписи — цветом текста, не цветом серии. */
import { num, pct, sum } from "@okno/shared";
import { tipProps } from "./kit";

export interface BarDatum {
  label: string;
  values: number[];
  colors?: string[];
  tip: string;
}

interface BarsProps {
  data: BarDatum[];
  series: { name: string; color: string }[];
  height?: number;
  threshold?: number;
  thresholdLabel?: string;
  format?: (v: number) => string;
  axisFormat?: (v: number) => string;
  labelEvery?: number;
  yTicks?: number;
  max?: number;
  label?: string;
  marker?: { index: number; label: string };
}

export function Bars({ data, series, height = 200, threshold, thresholdLabel, format = num, axisFormat, labelEvery = 1, yTicks = 3, max: maxOpt, label, marker }: BarsProps) {
  // круглый шаг делений: 1, 2, 2.5 или 5 × 10ⁿ
  const dataMax = maxOpt || Math.max(threshold || 0, ...data.map((d) => sum(d.values))) || 1;
  const raw = dataMax / yTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const ticks = Math.ceil(dataMax / step);
  const max = ticks * step * 1.06;
  const p = (v: number) => ((v / max) * 100).toFixed(2) + "%";
  const fmt = axisFormat || format;
  return (
    <div className="bars" role="img" aria-label={label || ""} style={{ height }}>
      <div className="bars-plot">
        {Array.from({ length: ticks + 1 }, (_, i) => (
          <div key={i} className={"bars-grid" + (i === 0 ? " is-base" : "")} style={{ bottom: p(step * i) }}>
            <span>{fmt(step * i)}</span>
          </div>
        ))}
        {threshold ? (
          <div className="bars-threshold" style={{ bottom: p(threshold) }}>
            <span>{thresholdLabel || ""}</span>
          </div>
        ) : null}
        <div className="bars-cols">
          {data.map((d, i) => (
            <div key={i} className="bars-col" {...tipProps(d.tip)}>
              {marker && marker.index === i ? (
                <div className="bars-marker">
                  <span>{marker.label}</span>
                </div>
              ) : null}
              <div className="bars-stack">
                {d.values.map((v, k) => (v > 0 ? <span key={k} style={{ height: p(v), background: d.colors ? d.colors[k] : series[k].color }} /> : null))}
              </div>
              {i % labelEvery === 0 ? <span className="bars-x">{d.label}</span> : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Воронка: горизонтальные полосы, значение и доля от первого шага. */
export function Funnel({ steps }: { steps: { label: string; value: number }[] }) {
  const max = steps[0]?.value || 1;
  return (
    <div className="funnel">
      {steps.map((s, i) => {
        const w = Math.max(2, (s.value / max) * 100);
        const conv = i === 0 ? "" : pct(s.value / max, 1);
        return (
          <div key={s.label} className="funnel-row" {...tipProps(s.label + "\n" + num(s.value) + (conv ? " · " + conv + " от отправленных" : ""))}>
            <span className="funnel-label">{s.label}</span>
            <span className="funnel-track">
              <span className="funnel-bar" style={{ width: w + "%" }} />
            </span>
            <span className="funnel-val num">
              {num(s.value)}
              {conv ? <span className="muted"> {conv}</span> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Тепловая карта: rows × cols, значение 0..1, один оттенок от светлого к тёмному. */
export function Heatmap({ rows, cols, values, tip }: { rows: string[]; cols: string[]; values: number[][]; tip: (r: number, c: number, v: number) => string }) {
  return (
    <div className="heatmap" style={{ gridTemplateColumns: "36px repeat(" + cols.length + ",minmax(0,1fr))" }}>
      <span />
      {cols.map((c, i) => (
        <span key={"c" + i} className="hm-col">
          {i % 2 === 0 ? c : ""}
        </span>
      ))}
      {rows.map((r, ri) => [
        <span key={"r" + ri} className="hm-row">
          {r}
        </span>,
        ...cols.map((_, ci) => {
          const v = values[ri][ci];
          return (
            <span
              key={ri + "-" + ci}
              className="hm-cell"
              style={{ background: "color-mix(in srgb, var(--primary) " + Math.round(8 + v * 82) + "%, var(--panel))" }}
              {...tipProps(tip(ri, ci, v))}
            />
          );
        }),
      ])}
    </div>
  );
}
